import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { saveAs } from 'file-saver';
import { CreditModeProvider, useCreditMode } from './CreditModeContext';
import { monthOptions } from './aiUsageShared';
import DailyUsageChart from './DailyUsageChart';
import {
  mapOverview,
  mapThreads,
  mapThreadMessages,
  mapDailyUsage,
  fillDailyGaps,
  countActiveDays,
  filterThreadsForPeriod,
} from './creditFieldMap';
import {
  getReportingOverview,
  getThreads,
  getThread,
  getDailyUsage,
} from '../../services/api/eiCreditsService';
import { currentPeriod, monthLabel, formatPercent } from '../../utils/creditFormat';
import { sa, Card, SelectPill, CreditCostToggle, SectionStates } from '../superAdminUi';
import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { SideMenu } from '../layouts/SideMenu';
import { Header } from '../layouts/Header';

// Conversation Analytics — per-user detail. Reached from the AI Usage tables via
// /ai-usage-analytics?userId=&scope=&id=. Presents headline stats, a searchable conversation
// list whose rows expand inline to the full chat log, and a daily credit-usage bar chart —
// laid out to the approved Figma with the shared Super Admin design system.

const useQuery = () => new URLSearchParams(useLocation().search);

// --- Layout -----------------------------------------------------------------

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
  font-family: ${sa.font.family};
`;
// The page is a drill-in three levels deep, so it says where you are as well as how to leave. The
// first crumb is a real link back to the section the drill-in came from — the browser-back button it
// replaced could only ever go back, never tell you where "back" was.
const Crumbs = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  align-self: flex-start;
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.textGray};
`;
const CrumbLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  font-weight: ${sa.font.semibold};
  font-size: 13px;
  color: ${sa.color.teal};

  svg {
    font-size: 17px;
  }
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${sa.color.teal};
    outline-offset: 2px;
    border-radius: ${sa.radius.sm};
  }
`;
const CrumbHere = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
`;
const CrumbSep = styled.span`
  color: ${sa.color.stroke};
`;

// --- User header card -------------------------------------------------------

const HeaderCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
`;
const UserName = styled.h1`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 20px;
  color: ${sa.color.text};
  margin: 0;
`;
const MetaLabel = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${sa.color.textGray};
  margin: 0;
`;
const MetaValue = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
  margin: 4px 0 0;
`;

// --- Stat tiles -------------------------------------------------------------

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const TileLabel = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 13px;
  color: ${sa.color.textGray};
  margin: 0;
`;
const TileValue = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.extrabold};
  font-size: 30px;
  line-height: 1.1;
  color: ${(p) => p.$color};
  margin: 10px 0 0;
`;
const TileNote = styled.p`
  font-family: ${sa.font.family};
  font-size: 12px;
  color: ${sa.color.textGray};
  margin: 12px 0 0;
`;

function StatTile({ label, value, color, note }) {
  return (
    <Card $pad='20px 24px'>
      <TileLabel>{label}</TileLabel>
      <TileValue $color={color}>{value}</TileValue>
      <TileNote>{note}</TileNote>
    </Card>
  );
}

// --- Controls row -----------------------------------------------------------

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
`;
const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const SearchBox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    font-size: 18px;
    color: ${sa.color.textGray};
    pointer-events: none;
  }
  input {
    width: 260px;
    max-width: 100%;
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 9px 12px 9px 36px;

    &::placeholder {
      color: ${sa.color.textGray};
    }
    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

// --- Conversations table ----------------------------------------------------

const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const CountTitle = styled.h2`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 18px;
  color: ${sa.color.text};
  margin: 0;
`;
const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: ${sa.color.purple};
  color: ${sa.color.white};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 12px;
`;
const TableWrap = styled.div`
  width: 100%;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.md};
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: ${sa.font.family};

  thead th {
    background: ${sa.color.purple};
    color: ${sa.color.white};
    font-weight: ${sa.font.bold};
    font-size: 13px;
    text-align: left;
    padding: 14px ${sa.space.cell};
    white-space: nowrap;
  }
  td {
    padding: 14px ${sa.space.cell};
    font-size: 13px;
    color: ${sa.color.textGray};
    vertical-align: middle;
  }
`;
const DataRow = styled.tr`
  background: ${(p) => (p.$alt ? sa.color.rowAlt : sa.color.white)};
  border-bottom: 1px solid ${sa.color.stroke};

  td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const TitleCell = styled.td`
  color: ${sa.color.text} !important;
  font-weight: ${sa.font.semibold};
`;
const Teal = styled.span`
  color: ${sa.color.teal};
  font-weight: ${sa.font.bold};
`;
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: ${(p) => p.$color || sa.color.textGray};
  line-height: 0;

  svg {
    font-size: 20px;
  }
  &:hover {
    color: ${sa.color.teal};
  }
`;
const EmptyCell = styled.td`
  text-align: center;
  padding: 28px !important;
  white-space: normal !important;
  color: ${sa.color.textGray};
`;

// --- Expanded conversation panel --------------------------------------------

const PanelCell = styled.td`
  padding: 0 !important;
  background: ${sa.color.white};
  border-bottom: 1px solid ${sa.color.stroke};
`;
const Panel = styled.div`
  padding: 20px 24px 24px;
  background: #fbfbfc;
`;
const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const PanelTitle = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.purple};
`;
const PanelMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin-right: auto;
  margin-left: 16px;
`;
const Reaction = styled.span`
  font-family: ${sa.font.family};
  font-size: 12px;
  color: ${sa.color.textGray};
  white-space: nowrap;
`;
const CloseLink = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.teal};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
const Bubble = styled.div`
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.md};
  padding: 14px 16px;
  margin-bottom: 12px;
`;
const BubbleHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(p) => p.$bg};

  svg {
    color: ${sa.color.white};
    font-size: 16px;
  }
`;
const AuthorName = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 13px;
  color: ${sa.color.text};
`;
const Timestamp = styled.span`
  font-family: ${sa.font.family};
  font-size: 12px;
  color: ${sa.color.textGray};
`;
const BubbleBody = styled.p`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.text};
  line-height: 1.5;
  margin: 0;
`;
const PanelFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;
const ViewLogButton = styled.button`
  border: 1px solid ${sa.color.stroke};
  background: ${sa.color.white};
  cursor: pointer;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 13px;
  color: ${sa.color.text};
  padding: 8px 18px;
  border-radius: ${sa.radius.sm};

  &:hover {
    border-color: ${sa.color.teal};
    color: ${sa.color.teal};
  }
`;
const PanelState = styled.p`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${(p) => p.$color || sa.color.textGray};
  margin: 0;
  padding: 8px 0;
`;

// --- Daily chart card -------------------------------------------------------

const ChartHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;
const ChartTitle = styled.h2`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 16px;
  color: ${sa.color.text};
  margin: 0;
`;
const Legend = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${sa.font.family};
  font-size: 12px;
  color: ${sa.color.textGray};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: ${sa.color.teal};
  }
`;

// --- Footnote ---------------------------------------------------------------

const Footnote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fdf9ec;
  border: 1px solid #f2e7c4;
  border-radius: ${sa.radius.sm};
  padding: 12px 14px;
  font-family: ${sa.font.family};
  font-size: 12.5px;
  color: #8a6d1f;
`;

// --- Formatting helpers -----------------------------------------------------

const formatLongDate = (iso) => {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s || '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

const formatTime = (ts) => {
  if (!ts) return '';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
};

const isUser = (role) => String(role || '').toLowerCase().includes('user');

function ThreadRow({ thread, period, alt }) {
  const { format } = useCreditMode();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logError, setLogError] = useState(null);

  const loadMessages = useCallback(async () => {
    const raw = await getThread(thread.id, { period, include: 'messages' });
    const mapped = mapThreadMessages(raw);
    setMessages(mapped);
    return mapped;
  }, [thread.id, period]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null && !loadingLog) {
      setLoadingLog(true);
      setLogError(null);
      try {
        await loadMessages();
      } catch (err) {
        setLogError(err?.message || 'Could not load this conversation.');
      } finally {
        setLoadingLog(false);
      }
    }
  };

  // Export / "View Full Log" both save the full transcript for the row.
  const exportThread = async () => {
    let log = messages;
    if (log === null) log = await loadMessages();
    const lines = (log || []).map((m) => `${m.role}: ${m.content}`);
    const header = `${thread.title || 'Conversation'} — ${String(thread.date || '').slice(0, 10)}`;
    const blob = new Blob([`${header}\n\n${lines.join('\n\n')}`], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `conversation-${thread.id}.txt`);
  };

  return (
    <>
      <DataRow $alt={alt}>
        <td>{formatLongDate(thread.date)}</td>
        <TitleCell title={thread.title || 'Untitled conversation'}>
          {thread.title || 'Untitled conversation'}
          {!thread.ownerName && ' *'}
        </TitleCell>
        <td>{formatPercent(thread.usagePct)}</td>
        <td>{thread.messages}</td>
        <td>
          <Teal>{format(thread.credits)}</Teal>
        </td>
        <td>
          <IconButton type='button' onClick={exportThread} aria-label='Export conversation'>
            <FileDownloadIcon />
          </IconButton>
        </td>
        <td>
          <IconButton type='button' onClick={toggle} aria-label={open ? 'Collapse conversation' : 'Expand conversation'}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
      </DataRow>
      {open && (
        <tr>
          <PanelCell colSpan={7}>
            <Panel>
              <PanelHead>
                <PanelTitle>Conversation Thread</PanelTitle>
                <PanelMeta>
                  {/* Reaction counts ride along on the thread payload already fetched for the
                      list, so showing them costs no extra request. */}
                  <Reaction title='Positive feedback'>👍 {thread.positiveFeedback}</Reaction>
                  <Reaction title='Negative feedback'>👎 {thread.negativeFeedback}</Reaction>
                  {formatTime(thread.lastMessageAt) && (
                    <Reaction>Last message {formatTime(thread.lastMessageAt)}</Reaction>
                  )}
                </PanelMeta>
                <CloseLink type='button' onClick={() => setOpen(false)}>
                  [Close]
                </CloseLink>
              </PanelHead>

              {loadingLog && (
                <PanelState>
                  <CircularProgress size={18} sx={{ color: sa.color.teal }} />
                </PanelState>
              )}
              {logError && <PanelState $color={sa.color.pink}>{logError}</PanelState>}

              {messages &&
                messages.map((m, i) => {
                  const user = isUser(m.role);
                  return (
                    <Bubble key={i}>
                      <BubbleHead>
                        <Author>
                          <Avatar $bg={user ? sa.color.purple : sa.color.teal}>
                            {user ? <PersonIcon /> : <SmartToyIcon />}
                          </Avatar>
                          <AuthorName>{user ? 'User' : 'AI Assistant'}</AuthorName>
                        </Author>
                        {formatTime(m.timestamp) && <Timestamp>{formatTime(m.timestamp)}</Timestamp>}
                      </BubbleHead>
                      <BubbleBody>{m.content}</BubbleBody>
                    </Bubble>
                  );
                })}
              {messages && messages.length === 0 && (
                <PanelState>No messages captured for this conversation.</PanelState>
              )}

              {messages && messages.length > 0 && (
                <PanelFooter>
                  <ViewLogButton type='button' onClick={exportThread}>
                    View Full Log
                  </ViewLogButton>
                </PanelFooter>
              )}
            </Panel>
          </PanelCell>
        </tr>
      )}
    </>
  );
}

function AnalyticsInner() {
  const history = useHistory();
  const query = useQuery();
  const { mode, setMode, format } = useCreditMode();

  const userId = query.get('userId');
  const scope = query.get('scope');
  const scopeId = query.get('id');

  // Where this drill-in came from. AI Usage is a tab on the Dashboard, on School Detail and on Group
  // Detail, so the parent depends on the scope the caller drilled in with — the same scope that
  // decides which sidebar item stays lit.
  const parent = useMemo(() => {
    if (scope === 'school' && scopeId) {
      return { label: 'Schools', to: `/school-detail?schoolId=${encodeURIComponent(scopeId)}` };
    }
    if (scope === 'group' && scopeId) {
      return { label: 'Groups', to: `/group-detail?matId=${encodeURIComponent(scopeId)}` };
    }
    return { label: 'AI Usage', to: '/super-admin-dashboard' };
  }, [scope, scopeId]);

  const [period, setPeriod] = useState(currentPeriod());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [overview, setOverview] = useState(null);
  const [threads, setThreads] = useState([]);
  const [daily, setDaily] = useState([]);
  const [activeDays, setActiveDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [threadsError, setThreadsError] = useState(null);

  const months = useMemo(
    () => monthOptions().map((p) => ({ value: p, label: monthLabel(p) })),
    []
  );

  const daysInPeriod = useMemo(() => {
    const [y, m] = period.split('-').map(Number);
    return y && m ? new Date(Date.UTC(y, m, 0)).getUTCDate() : 0;
  }, [period]);

  // Debounce the conversation search so typing doesn't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // The overview and daily series depend on the user/scope/period only — not the thread
  // search — so they load on mount and period change, never per keystroke.
  const loadOverview = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError('No user selected.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Analytics are always per-user: the overview, daily series and threads all scope to the
      // user's GUID. (The org scope/id in the URL is only the drill-in context, NOT an id for
      // these calls — passing it as `id` sent a GUID to a school-scoped endpoint, which the API
      // rejected with "School Id must be numeric".)
      const [overviewRaw, dailyRaw] = await Promise.all([
        getReportingOverview({ scope: 'user', id: userId, period }),
        getDailyUsage({ scope: 'user', id: userId, months: [period] }),
      ]);
      setOverview(mapOverview(overviewRaw));
      // Count the real activity days BEFORE gap-filling pads the month with zero-usage days,
      // because the overview endpoint carries no active-day figure of its own.
      const realDays = mapDailyUsage(dailyRaw);
      setActiveDays(countActiveDays(realDays));
      setDaily(fillDailyGaps(realDays, period));
    } catch (err) {
      setError(err?.message || 'Could not load conversation analytics.');
      setOverview(null);
      setActiveDays(0);
      setDaily([]);
    } finally {
      setLoading(false);
    }
  }, [userId, period]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // Only the conversation list is re-queried as the search changes.
  const loadThreads = useCallback(async () => {
    if (!userId) return;
    setThreadsLoading(true);
    setThreadsError(null);
    try {
      const threadsRaw = await getThreads({
        scope: 'user',
        id: userId,
        period,
        // The threads endpoint only allows include=breakdown|messages; breakdown gives the
        // per-conversation credit/message aggregates the list needs (summary was rejected).
        include: 'breakdown',
        search: search || undefined,
      });
      setThreads(filterThreadsForPeriod(mapThreads(threadsRaw), period));
    } catch (err) {
      setThreadsError(err?.message || 'Could not load conversations.');
      setThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  }, [userId, period, search]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const hasUnnamedThread = useMemo(() => threads.some((t) => !t.ownerName), [threads]);

  return (
    <Page>
      <Crumbs aria-label='Breadcrumb'>
        <CrumbLink type='button' onClick={() => history.push(parent.to)}>
          <ArrowBackIcon />
          {parent.label}
        </CrumbLink>
        {overview?.schoolName && (
          <>
            <CrumbSep>›</CrumbSep>
            <span>{overview.schoolName}</span>
          </>
        )}
        <CrumbSep>›</CrumbSep>
        <CrumbHere aria-current='page'>{overview?.userName || 'User'}</CrumbHere>
      </Crumbs>

      <SectionStates loading={loading} error={error} onRetry={loadOverview} />

      {!loading && !error && (
        <>
          <HeaderCard>
            <UserName>{overview?.userName || 'User'}</UserName>
            <div>
              <MetaLabel>School</MetaLabel>
              <MetaValue>{overview?.schoolName || '—'}</MetaValue>
            </div>
            <div>
              <MetaLabel>Role</MetaLabel>
              <MetaValue>{overview?.role || '—'}</MetaValue>
            </div>
          </HeaderCard>

          <Tiles>
            <StatTile
              label='Total Messages'
              value={overview?.totalMessages ?? 0}
              color={sa.color.purple}
              note='Across all schools'
            />
            <StatTile
              label={mode === 'cost' ? 'Cost' : 'Credits Used'}
              value={format(overview?.creditsUsed ?? 0, { compact: false })}
              color={sa.color.teal}
              note='Total this month'
            />
            <StatTile
              label='Active Days'
              value={`${activeDays}/${daysInPeriod}`}
              color={sa.color.pink}
              note='Consistent engagement'
            />
          </Tiles>

          <Controls>
            <SelectPill value={period} onChange={setPeriod} options={months} ariaLabel='Month' testId='month-dropdown' />
            <ControlsRight>
              <SearchBox>
                <SearchIcon />
                <input
                  type='text'
                  placeholder='Search conversations...'
                  aria-label='Search conversations'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </SearchBox>
              <CreditCostToggle value={mode} onChange={setMode} />
            </ControlsRight>
          </Controls>

          <CountRow>
            <CountTitle>Conversations</CountTitle>
            <CountBadge>{threads.length}</CountBadge>
          </CountRow>

          {threadsLoading || threadsError ? (
            <SectionStates loading={threadsLoading} error={threadsError} onRetry={loadThreads} />
          ) : (
            <TableWrap>
              <Table>
                <colgroup>
                  <col style={{ width: '130px' }} />
                  <col />
                  <col style={{ width: '70px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '90px' }} />
                  <col style={{ width: '90px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Thread Title</th>
                    <th>%</th>
                    <th>Messages</th>
                    <th>Credits</th>
                    <th>Export</th>
                    <th>Expand</th>
                  </tr>
                </thead>
                <tbody>
                  {threads.length === 0 ? (
                    <tr>
                      {/* Naming the month matters on a page that opens on the CURRENT one: early in
                          a month there is usually nothing yet, and an unqualified "no conversations"
                          reads as a broken page rather than an empty month. */}
                      <EmptyCell colSpan={7}>
                        No conversations in {monthLabel(period)}. Choose another month to see earlier
                        activity.
                      </EmptyCell>
                    </tr>
                  ) : (
                    threads.map((t, i) => (
                      <ThreadRow key={t.id} thread={t} period={period} alt={i % 2 === 1} />
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}

          <Card>
            <ChartHead>
              <ChartTitle>Daily Credit Usage — {monthLabel(period)}</ChartTitle>
              <Legend>AI Interactions</Legend>
            </ChartHead>
            <DailyUsageChart days={daily} mode={mode} />
          </Card>

          {hasUnnamedThread && (
            <Footnote>
              Some older conversations may not show an owner name — these appear as System / async
              tasks in usage totals.
            </Footnote>
          )}
        </>
      )}
    </Page>
  );
}

// Inside the portal chrome, like every other admin screen. Rendering the bare page was what made a
// drill-in feel like leaving the product: the sidebar and header vanished, and the content lost the
// canvas tint and padding the rest of the portal shares.
function ConversationAnalytics() {
  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <CreditModeProvider>
            <AnalyticsInner />
          </CreditModeProvider>
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

export default ConversationAnalytics;
